import React, { useEffect, forwardRef, useImperativeHandle, useState } from "react";
import { useFormik, getIn } from 'formik';

import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import Grid from "@mui/material/Grid";
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Typography } from "@mui/material";

import { frequencyPaymentTypes, units } from "../helpers/dataOptions";
import firstCharToUpper, { makePlural } from "../helpers/stringFormat";
import { TextFieldCustom } from "../helpers/formFields";
import { formatNumberThsndAndDec } from "../helpers/stringFormat";
import { tooltip, generalTooltipText } from "../helpers/tooltip";
import { WebpageInput } from "../helpers/WebpageInput";
import { queryValueProposition } from "../../actions/valueActions";

const revenueModels = [
    {
        value: 'subscription',
        label: 'Subscription-based',
    },
    {
        value: 'transactional',
        label: 'Transactional',
    }
];

const ProductCreateForm = forwardRef(({ currency, editProductId, editProduct, formActions, onSubmit }, ref) => {
    const [open, setOpen] = useState(false);
    const [frequencyOpen, setFrequencyOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const tooltipText = {
        uom: "Unit of Measure defines the basis on which pricing is calculated (e.g., per kilogram, per user, per meter, or per ton). This field is optional and can be left blank if not applicable.",
        frequency: "Price frequency defines the recurring interval over which pricing is calculated or billed. It can be set as daily, monthly, quarterly, or yearly, and indicates how frequently charges are applied.",
        alternative: "The Competitive alternative is the next best alternative to your product. If you are competing with more than one product choose one or create a mix of them. It can also be an internal process e.g. doing things manually in Excel.",
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleOpen = () => {
        setOpen(true);
    };

    const handleFrequencyClose = () => {
        setFrequencyOpen(false);
    };

    const handleFrequencyOpen = () => {
        setFrequencyOpen(true);
    };

    const handleRevenueModelChange = (event) => {
        formik.setFieldValue('revenue_model', event.target.value);
    };

    const handleFrequencyChange = (event) => {
        formik.setFieldValue('frequency', event.target.value);
    };

    const initialValues = {
        name: '',
        description: '',
        value_prop: '',
        revenue_model: revenueModels[0].value,
        frequency: frequencyPaymentTypes[0].label,
        unit: '',
        status_quo: true,
        alternative: 'Status quo',
        price_alternative: '0',
        currency: currency?.symbol,
        annual_purchase: 0
    };

    const isNumeric = (value) => {
        return /^-?\d{1,3}(,\d{3})*(\.\d+)?$/.test(value);
    };

    const isPositive = (value) => {
        return parseFloat(value.toString().replace(/,/g, '')) > 0;
    };

    const validate = (values) => {
        const errors = {};
        if (!values.name) {
            errors.name = 'Name is required';
        }
        if (!values.value_prop) {
            errors.value_prop = 'Value proposition is required';
        }
        if (values.revenue_model === 'transactional' && !values.unit) {
            errors.unit = 'Unit cost basis is required';
        }
        if (!values.alternative) {
            errors.alternative = 'Alternative is required';
        }
        if (values.unit && !isNumeric(values.annual_purchase)) {
            errors.annual_purchase = 'Must be numeric value';
        } else if (values.unit && !isPositive(values.annual_purchase)) {
            errors.annual_purchase = 'Must be positive';
        }
        if (!values.status_quo && (values.unit && !isNumeric(values.price_alternative))) {
            errors.price_alternative = 'Must be numeric value';
        } else if (!values.status_quo && (values.unit && !isPositive(values.price_alternative))) {
            errors.price_alternative = 'Must be positive';
        }

        return errors;
    };

    const formik = useFormik({
        initialValues: initialValues,
        validate: validate,
        onSubmit: onSubmit,
    });

    useImperativeHandle(ref, () => ({
        resetForm() {
            formik.handleReset();
        },
    }));

    useEffect(() => {
        if (editProductId) {
            formik.setFieldValue('name', editProduct['name']);
            // formik.setFieldValue('description', editProduct['description'])
            formik.setFieldValue('value_prop', editProduct['value_prop']);
            formik.setFieldValue('revenue_model', editProduct['revenue_model']);
            formik.setFieldValue('frequency', editProduct['frequency']);
            formik.setFieldValue('unit', editProduct['unit']);
            formik.setFieldValue('status_quo', editProduct['status_quo']);
            formik.setFieldValue('alternative', editProduct['alternative']);
            formik.setFieldValue('price_alternative', formatNumberThsndAndDec(editProduct['price_alternative']));
            formik.setFieldValue('currency', editProduct['currency']);
            formik.setFieldValue('annual_purchase', formatNumberThsndAndDec(editProduct['annual_purchase']));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editProductId]);

    useEffect(() => {
        if (formik.values.revenue_model === 'transactional') {
            formik.setFieldValue('frequency', '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.revenue_model]);

    const findValuePropSubmit = async (values) => {
        setLoading(true);
        // let success = false;
        const result = await queryValueProposition(values);
        // success = result?.success;
        formik.setFieldValue('value_prop', result?.data.value_proposition);
        setLoading(false);
    };

    return (
        <Box component="form" onSubmit={formik.handleSubmit} sx={{ maxWidth: 700 }}>
            <Grid container rowGap={1} columnGap={1}>
                {currentPage === 1 && (
                    <>
                        {/* Page 1: Up to Value proposition */}
                        <Grid item xs={4}>
                            <Stack direction="column">
                                <Typography variant="caption">
                                    Product name
                                </Typography>
                                <TextFieldCustom
                                    name="name"
                                    value={formik.values.name}
                                    onChange={formik.handleChange}
                                    size="small"
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.name && Boolean(formik.errors.name)}
                                    helperText={formik.touched.name && formik.errors.name}
                                />
                            </Stack>
                        </Grid>
                        <Grid item xs={7} />
                        <Grid item xs={12} sm={10}>
                            <WebpageInput
                                product={formik.values.name}
                                onSubmit={findValuePropSubmit}
                                loading={loading}
                            />
                        </Grid>
                        <Grid item xs={10}>
                            <Stack direction="column">
                                <Typography variant="caption">
                                    Value proposition
                                </Typography>
                                <TextFieldCustom
                                    name="value_prop"
                                    value={formik.values.value_prop}
                                    onChange={formik.handleChange}
                                    size="small"
                                    multiline
                                    minRows={4}
                                    onBlur={formik.handleBlur}
                                    error={formik.touched.value_prop && Boolean(formik.errors.value_prop)}
                                    helperText={formik.touched.value_prop && formik.errors.value_prop}
                                />
                            </Stack>
                        </Grid>
                        {/* Next Button at bottom right on Page 1 */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                                <Button endIcon={<ChevronRightRoundedIcon />} onClick={() => setCurrentPage(2)} disabled={loading} sx={{ textTransform: 'none' }}>
                                    Next
                                </Button>
                            </Box>
                        </Grid>
                    </>
                )}
                {currentPage === 2 && (
                    <>
                        {/* Page 2: The rest of the form */}
                        <Grid item xs={4}>
                            <Stack direction="column">
                                <Typography variant="caption">
                                    Revenue model{tooltip(generalTooltipText.revenueModel)}
                                </Typography>
                                <Select
                                    open={open}
                                    onClose={handleClose}
                                    onOpen={handleOpen}
                                    value={formik.values.revenue_model}
                                    onChange={handleRevenueModelChange}
                                    size="small"
                                >
                                    {revenueModels.map((model) => (
                                        <MenuItem key={model.value} value={model.value}>
                                            {model.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </Grid>
                        <Grid item xs={4}>
                            {formik.values.revenue_model === 'subscription' && (
                                <Stack direction="column">
                                    <Typography variant="caption">
                                        Pricing frequency{tooltip(tooltipText.frequency)}
                                    </Typography>
                                    <Select
                                        open={frequencyOpen}
                                        onClose={handleFrequencyClose}
                                        onOpen={handleFrequencyOpen}
                                        value={formik.values.frequency}
                                        onChange={handleFrequencyChange}
                                        size="small"
                                    >
                                        {frequencyPaymentTypes.map((object) => (
                                            <MenuItem key={object.label} value={object.label}>
                                                {firstCharToUpper(object.label)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </Stack>
                            )}
                        </Grid>
                        <Grid item xs={4}>
                            <Stack direction="column">
                                <Typography variant="caption">
                                    Unit of Measure{tooltip(tooltipText.uom)}
                                </Typography>
                                <Autocomplete
                                    freeSolo
                                    disableClearable
                                    options={units.map(option => option.label)}
                                    size="small"
                                    value={formik.values.unit}
                                    onChange={(event, value) => formik.setFieldValue('unit', value)}
                                    onInputChange={(event, value) => formik.setFieldValue('unit', value)}
                                    renderInput={(params) => (
                                        <TextField
                                            error={getIn(formik.touched, 'unit') && Boolean(getIn(formik.errors, 'unit'))}
                                            helperText={getIn(formik.touched, 'unit') && getIn(formik.errors, 'unit')}
                                            value={formik.values.unit}
                                            onBlur={() => formik.setFieldTouched('unit', true)}
                                            {...params}
                                            InputProps={{
                                                ...params.InputProps,
                                                type: 'search',
                                            }}
                                        />
                                    )}
                                />
                            </Stack>
                            {formik.values.unit.length > 0 && (
                                <Stack direction="column">
                                    <Typography variant="caption">
                                        Typical customer's annual purchase volume
                                    </Typography>
                                    <TextFieldCustom
                                        name="annual_purchase"
                                        endAdornment={
                                            <Typography variant="caption">
                                                {makePlural(formik.values.unit)}
                                            </Typography>
                                        }
                                        value={formik.values.annual_purchase}
                                        onChange={(event) => {
                                            const { value } = event.target;
                                            const formattedValue = formatNumberThsndAndDec(value);
                                            formik.setFieldValue("annual_purchase", formattedValue || '');
                                        }}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.annual_purchase && Boolean(formik.errors.annual_purchase)}
                                        helperText={formik.touched.annual_purchase && formik.errors.annual_purchase}
                                    />
                                </Stack>
                            )}
                        </Grid>
                        <Grid item xs={4} />
                        {/* <Grid item xs={4}> */}
                        {/* Competitive alternative header + switch */}
                        <Grid item xs={12} sx={{ mt: 2 }}>
                            <Typography variant="caption">Competitive alternative</Typography>
                        </Grid>
                        <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Typography variant="caption">Status quo</Typography>
                            <Switch
                                size="small"
                                // invert so checked=true => thumb on right (Competitor)
                                checked={!formik.values.status_quo}
                                onChange={(e) => {
                                    const isCompetitor = e.target.checked;
                                    if (isCompetitor) {
                                        formik.setFieldValue('status_quo', false);
                                        // If editing existing, restore saved values; else blank
                                        formik.setFieldValue(
                                            'alternative',
                                            editProductId ? editProduct.alternative : ''
                                        );
                                        formik.setFieldValue(
                                            'price_alternative',
                                            editProductId
                                                ? formatNumberThsndAndDec(editProduct.price_alternative)
                                                : ''
                                        );
                                    } else {
                                        formik.setFieldValue('status_quo', true);
                                        formik.setFieldValue('alternative', 'Status quo');
                                        formik.setFieldValue('price_alternative', '0');
                                    }
                                }}
                                sx={{
                                    '& .MuiSwitch-thumb': { bgcolor: 'green' },
                                }}
                            />
                            <Typography variant="caption">Competitor</Typography>
                        </Grid>
                        {!formik.values.status_quo && (
                            <Grid item xs={4}>
                                <Stack direction="column">
                                    <Typography variant="caption">
                                        Name of Competitive alternative{tooltip(tooltipText.alternative)}
                                    </Typography>
                                    <TextFieldCustom
                                        name="alternative"
                                        value={formik.values.alternative}
                                        onChange={formik.handleChange}
                                        size="small"
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.alternative && Boolean(formik.errors.alternative)}
                                        helperText={formik.touched.alternative && formik.errors.alternative}
                                    />
                                </Stack>
                            </Grid>
                        )}
                        {!formik.values.status_quo && (
                            <Grid item xs={4}>
                                {(formik.values.unit.length > 0 || formik.values.revenue_model === 'subscription') && (
                                    <Stack direction="column">
                                        <Typography variant="caption">
                                            Price of Competitive alternative
                                        </Typography>
                                        <TextFieldCustom
                                            name="price_alternative"
                                            startAdornment={currency.symbol}
                                            endAdornment={
                                                <>
                                                    {formik.values.revenue_model === 'transactional' && (
                                                        <Typography variant="caption">
                                                            per {formik.values.unit}
                                                        </Typography>
                                                    )}
                                                    {formik.values.revenue_model === 'subscription' && (
                                                        <Typography variant="caption">
                                                            {formik.values.frequency}{formik.values.unit.length > 0 ? '/' + formik.values.unit : ''}
                                                        </Typography>
                                                    )}
                                                </>
                                            }
                                            value={formik.values.price_alternative}
                                            onChange={(event) => {
                                                const { value } = event.target;
                                                const formattedValue = formatNumberThsndAndDec(value);
                                                formik.setFieldValue("price_alternative", formattedValue || '');
                                            }}
                                            onBlur={formik.handleBlur}
                                            error={formik.touched.price_alternative && Boolean(formik.errors.price_alternative)}
                                            helperText={formik.touched.price_alternative && formik.errors.price_alternative}
                                        />
                                    </Stack>
                                )}
                            </Grid>
                        )}
                        {/* Navigation buttons on page 2 */}
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                                <Button startIcon={<ChevronLeftRoundedIcon />} onClick={() => setCurrentPage(1)} sx={{ textTransform: 'none' }}>
                                    Back
                                </Button>
                                {formActions(loading)}
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
});

export default ProductCreateForm;